import {
  type GetApiGreetingsQueryParams,
  type GreetingModel,
  type GreetingModelPagedResult,
  getApiGreetings,
} from '@/shared/api'
import {
  DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
} from '@/shared/config'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/shared/lib/query'
import { type Query, createQuery } from '@farfetched/core'
import {
  type EventCallable,
  type Store,
  type StoreWritable,
  combine,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { debounce } from 'patronum'

export interface GreetingsQueryModelBase {
  // Availability
  $isAvailable: StoreWritable<boolean>
  availableSet: EventCallable<void>
  unavailableSet: EventCallable<void>
  //   Filters
  $filters: StoreWritable<GetApiGreetingsQueryParams>
  filtersInit: EventCallable<Partial<GetApiGreetingsQueryParams>>
  filtersChanged: EventCallable<Partial<GetApiGreetingsQueryParams>>
  filtersReset: EventCallable<void>
  //   Search
  $search: StoreWritable<string | null>
  searchChanged: EventCallable<string>
  searchReset: EventCallable<void>
  //   Sort
  $sort: StoreWritable<GreetingsSortState>
  sortChanged: EventCallable<GreetingsSortState>
  sortReset: EventCallable<void>
  //   Params
  $params: Store<{
    params: GetApiGreetingsQueryParams
  }>
  // Query
  query: Query<
    {
      params?: GetApiGreetingsQueryParams
    },
    GreetingModelPagedResult,
    unknown
  >
  refreshed: EventCallable<void>
  // Accumulated
  $accumulatedData: StoreWritable<GreetingModel[]>
}

export interface GreetingsQueryModelWithPagination
  extends GreetingsQueryModelBase {
  $page: StoreWritable<number>
  pageChanged: EventCallable<number>
  pageReset: EventCallable<void>
  $pageSize: StoreWritable<number>
  pageSizeChanged: EventCallable<number>
}

interface GreetingsQueryModelParams {
  initialFilters?: Partial<GetApiGreetingsQueryParams>
  usePagination: boolean
  accumulate: boolean
}

export interface GreetingsSortState {
  field: 'name' | 'date' | 'priority' | null
  order: 'asc' | 'desc' | null
}

export function createGreetingsQueryModel(
  {
    initialFilters = {},
    usePagination = true,
    accumulate = false,
  }: GreetingsQueryModelParams = {
    initialFilters: {},
    usePagination: true,
    accumulate: false,
  },
): GreetingsQueryModelWithPagination | GreetingsQueryModelBase {
  // Query
  const query = createQuery({ handler: getApiGreetings })

  // Availability
  const availableSet = createEvent()
  const unavailableSet = createEvent()
  const $isAvailable = createStore(false)
    .on(availableSet, () => true)
    .on(unavailableSet, () => false)

  // Filters
  const filtersInit = createEvent<Partial<GetApiGreetingsQueryParams>>()
  const filtersChanged = createEvent<Partial<GetApiGreetingsQueryParams>>()
  const filtersReset = createEvent()
  const $filters = createStore<GetApiGreetingsQueryParams>(initialFilters)
    .on(filtersInit, (_, payload) => ({ ...initialFilters, ...payload }))
    .on(filtersChanged, (state, payload) => ({ ...state, ...payload }))
    .on(filtersReset, () => initialFilters)

  // Search
  const searchChanged = createEvent<string>()
  const searchReset = createEvent()
  const $search = createStore<string | null>(null)

  //   Sort
  const initialSort: GreetingsSortState = { field: null, order: null }
  const sortChanged = createEvent<GreetingsSortState>()
  const sortReset = createEvent()

  const $sort = createStore<GreetingsSortState>(initialSort)
    .on(sortChanged, (_, payload) => payload)
    .on(sortReset, () => initialSort)

  // Pagination

  const $page = createStore<number>(DEFAULT_PAGE)

  const pageChanged = createEvent<number>()
  const pageReset = createEvent()
  $page
    .on(pageChanged, (_, page) => Math.max(DEFAULT_PAGE, page))
    .on(pageReset, () => DEFAULT_PAGE)

  const $pageSize = createStore<number>(DEFAULT_PAGE_SIZE)
  const pageSizeChanged = createEvent<number>()
  $pageSize.on(pageSizeChanged, (_, size) => size)

  sample({
    source: query.$status,
    clock: [$filters, $search, $sort],
    filter: (status) => status !== 'initial',
    target: pageReset,
  })

  // Query params
  const $params = combine(
    $filters,
    $search,
    $page,
    $pageSize,
    $sort,
    (filters, search, page, pageSize, sort) => {
      // Filters
      const params: GetApiGreetingsQueryParams = { ...filters }
      if (search) params.SearchQuery = search.trim()
      // Sort
      if (sort.field && sort.order) {
        if (sort.field === 'name') {
          if (sort.order === 'asc') params.SortByName = true
          else params.SortByNameDesc = true
        }
        if (sort.field === 'date') {
          if (sort.order === 'asc') params.SortByDate = true
          else params.SortByDateDesc = true
        }
        if (sort.field === 'priority') {
          if (sort.order === 'asc') params.SortByPriority = true
          else params.SortByPriorityDesc = true
        }
      }
      // Pagination
      if (usePagination) {
        params.Limit = pageSize
        params.Offset = (page - 1) * pageSize
      }
      return { params }
    },
  )

  // Accumulation
  const initialAccumulatedData: GreetingModel[] = []
  const accumulatedDataReset = createEvent()
  const $accumulatedData = createStore<GreetingModel[]>(initialAccumulatedData)
    .on(query.finished.success, (state, { result }) => {
      if (accumulate) {
        const map = new Map(state.map((item) => [item.id, item]))
        for (const item of result.items) {
          map.set(item.id, item)
        }
        return Array.from(map.values())
      }
      return result.items
    })
    .on(accumulatedDataReset, () => initialAccumulatedData)

  // Debounce
  const paramsDebounced = debounce({
    source: $params,
    timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  })

  const searchChangedDebounced = debounce({
    source: searchChanged,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  const searchResetDebounced = debounce({
    source: searchReset,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  // Start query
  sample({
    clock: paramsDebounced,
    filter: $isAvailable,
    target: query.start,
  })

  //   Refresh
  const refreshed = createEvent()
  sample({
    source: $params,
    clock: refreshed,
    filter: $isAvailable,
    target: query.start,
  })

  // Change search

  $search
    .on(searchChangedDebounced, (_, search) => search)
    .reset(searchResetDebounced)

  // Reset logic
  sample({
    clock: unavailableSet,
    target: query.reset,
  })

  sample({
    clock: query.reset,
    target: [filtersReset, searchReset, sortReset, pageReset],
  })

  const model: GreetingsQueryModelBase = {
    // Availability
    $isAvailable,
    availableSet,
    unavailableSet,
    // Filters
    $filters,
    filtersInit,
    filtersChanged,
    filtersReset,
    // Search
    $search,
    searchChanged,
    searchReset,
    // Sort
    $sort,
    sortChanged,
    sortReset,
    // Params
    $params,
    // Query
    query,
    refreshed,
    // Accumulation
    $accumulatedData,
  }

  const modelWithPagination: GreetingsQueryModelWithPagination = {
    ...model,
    $page,
    pageChanged,
    pageReset,
    $pageSize,
    pageSizeChanged,
  }

  if (usePagination) {
    return modelWithPagination
  }

  return model
}
